import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Close } from "@mui/icons-material";
import { AlertProps, AlertTitle, Button, Divider, IconButton, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { selectCurrentRole } from "fond/api";
import {
  EXCEED_SUBSCRIPTION_QUOTA_KEY,
  EXPIRED_BANNER_KEY,
  PENDING_INVITATION_BANNER_KEY,
  SOON_TO_EXPIRED_BANNER_KEY,
  TRIAL_BANNER_KEY,
  TRIAL_TIER,
} from "fond/constants";
import { type Banner, type BannerType, useUserManagementBanner } from "fond/hooks/useCanIPlanner";
import { useInviteAcceptance } from "fond/invitation/useHandleInviteAcceptance";
import { formatDateStringWithTimezone } from "fond/utils/dates";
import AccountAvatar from "fond/widgets/AccountAvatar";

import { Banner as StyledBanner, ManageAccountButton } from "./TopBar.styles";

interface BannerContent {
  /**
   * The title of the banner, which is displayed in bold at the top.
   */
  title?: React.ReactNode;
  /**
   * The main message to be displayed on the banner.
   */
  text: string;
  /**
   * The action element to be displayed on the banner, such as a button or link.
   */
  action: React.ReactNode;
  /**
   * The severity level of the banner, which determines its color and icon. It can be one of "error", "warning", "info", or "success". If not specified, it defaults to "error".
   * @default "error"
   */
  severity?: AlertProps["severity"];
  /**
   * Whether the banner can be dismissed by the user. If false, the close button will be hidden and the banner cannot be dismissed.
   * @default true
   */
  dismissible?: boolean;
  /**
   * Optional custom icon to replace the default severity icon.
   */
  icon?: React.ReactNode;
}

const UserManagementBanner: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const isAdmin = useSelector(selectCurrentRole) === "admin";
  const { handleInviteAcceptance } = useInviteAcceptance();

  const onInviteAccept = async (invitationId: string) => {
    try {
      await handleInviteAcceptance(invitationId);
      enqueueSnackbar("Invitation accepted", { variant: "success" });
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar("Unable to accept invitation", { variant: "error" });
    }
  };

  const getBannerContent = (banner: Banner): BannerContent | undefined => {
    const { subscription, bannerType, currentBillingCycle, invitation } = banner;
    const manageAccountButton = isAdmin && (
      <ManageAccountButton data-testid="manage-account-button" color="inherit" to="/settings/account/subscription">
        MANAGE SUBSCRIPTION
      </ManageAccountButton>
    );

    if (bannerType === "soon to expire") {
      if (subscription?.Planner?.Tier === TRIAL_TIER) {
        return {
          text: `Your trial is expiring ${currentBillingCycle ? `on ${formatDateStringWithTimezone(currentBillingCycle.EndTime)}` : "soon"}. ${
            isAdmin ? "Upgrade to continue using our premium features." : "Contact your administrator for further assistance."
          }`,
          action: manageAccountButton,
        };
      }
      return {
        text: `Your subscription is expiring ${currentBillingCycle ? `on ${formatDateStringWithTimezone(currentBillingCycle.EndTime)}` : "soon"}. ${
          isAdmin ? "Renew as soon as possible to continue using our premium features." : "Contact your administrator for further assistance."
        }`,
        action: manageAccountButton,
      };
    } else if (bannerType === "expired") {
      if (currentBillingCycle?.Planner?.Tier === TRIAL_TIER) {
        return {
          text: `Your trial has ended. ${
            isAdmin ? "Upgrade to continue using our premium features." : "Contact your administrator for further assistance."
          }`,
          action: manageAccountButton,
        };
      }
      return {
        text: `Your subscription has expired. ${
          isAdmin ? "Renew as soon as possible to continue using our premium features." : "Contact your administrator for further assistance."
        }`,
        action: manageAccountButton,
      };
    } else if (bannerType === "trial") {
      return {
        text: `Your trial is active until ${currentBillingCycle ? formatDateStringWithTimezone(currentBillingCycle.EndTime) : "soon"}.`,
        action: (
          <ManageAccountButton
            data-testid="upgrade-button"
            color="inherit"
            onClick={() => analytics.track("banner.trial-active.upgrade.clicked")}
            to="/subscription/get-started"
          >
            UPGRADE
          </ManageAccountButton>
        ),
        severity: "info",
      };
    } else if (bannerType === "quota exceeded") {
      return {
        text: `Your subscription quota is nearly all used. ${
          isAdmin
            ? "Upgrade your plan or purchase data bundles as soon as possible to continue using our premium features."
            : "Contact your administrator for further assistance."
        }`,
        action: manageAccountButton,
      };
    } else if (bannerType === "over allocation") {
      return {
        title: "License Limit Exceeded",
        text: `You've assigned more licenses than your subscription includes. Please purchase additional licenses or unassign some users.`,
        severity: "warning",
        action: manageAccountButton,
        dismissible: false,
      };
    } else if (bannerType === "pending invitation" && invitation) {
      return {
        title: `Invitation to join ${invitation.Account.Name}`,
        text: `${invitation.Creator.Email} has invited you to join their workspace. Accept the invitation to access shared projects and collaborate with your team.`,
        severity: "info",
        icon: <AccountAvatar account={invitation.Account} sx={{ width: 24, height: 24 }} />,
        action: (
          <>
            <ManageAccountButton data-testid="view-invitations-button" color="inherit" to="/settings/profile/account-invites">
              VIEW INVITATION
            </ManageAccountButton>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Button data-testid="accept-invitation-button" onClick={() => onInviteAccept(invitation.ID)} sx={{ p: 1 }}>
              ACCEPT
            </Button>
          </>
        ),
      };
    }

    return undefined;
  };

  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(new Set());
  const { banners } = useUserManagementBanner();
  const containerRef = useRef<HTMLDivElement>(null);

  // Load tracked banners from sessionStorage to persist across component remounts
  const getTrackedBanners = (): Set<BannerType> => {
    try {
      const stored = sessionStorage.getItem("tracked_banners");
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  };
  const [trackedBanners, setTrackedBanners] = useState<Set<BannerType>>(getTrackedBanners);

  const handleClose = (banner: Banner) => {
    const { bannerType, invitation } = banner;

    if (bannerType === "trial") {
      sessionStorage.setItem(TRIAL_BANNER_KEY, "true");
    } else if (bannerType === "soon to expire") {
      sessionStorage.setItem(SOON_TO_EXPIRED_BANNER_KEY, "true");
    } else if (bannerType === "expired") {
      sessionStorage.setItem(EXPIRED_BANNER_KEY, "true");
    } else if (bannerType === "quota exceeded") {
      sessionStorage.setItem(EXCEED_SUBSCRIPTION_QUOTA_KEY, "true");
    } else if (bannerType === "pending invitation" && invitation) {
      // Store dismissal per account ID to allow dismissing individual invitations
      const invitationKey = `${PENDING_INVITATION_BANNER_KEY}_${invitation.Account.ID}`;
      sessionStorage.setItem(invitationKey, "true");
    }

    // Create unique key for dismissal tracking
    const dismissalKey = bannerType === "pending invitation" && invitation ? `${bannerType}-${invitation.Account.ID}` : bannerType;
    setDismissedBanners((prev) => new Set(prev).add(dismissalKey));
  };

  const visibleBanners = useMemo(
    () =>
      banners.filter((banner) => {
        const dismissalKey =
          banner.bannerType === "pending invitation" && banner.invitation
            ? `${banner.bannerType}-${banner.invitation.Account.ID}`
            : banner.bannerType;
        return !dismissedBanners.has(dismissalKey);
      }),
    [banners, dismissedBanners]
  );

  // Track when banners are shown (only once per session)
  useEffect(() => {
    let hasNewTracking = false;
    const newTracked = new Set(trackedBanners);

    visibleBanners.forEach((banner) => {
      if (!newTracked.has(banner.bannerType)) {
        analytics.track("banner.user-management.shown", { bannerType: banner.bannerType });
        newTracked.add(banner.bannerType);
        hasNewTracking = true;
      }
    });

    if (hasNewTracking) {
      setTrackedBanners(newTracked);
      sessionStorage.setItem("tracked_banners", JSON.stringify([...newTracked]));
    }
  }, [visibleBanners, trackedBanners]);

  // Force a resize measurement when the number of visible banners changes
  // This ensures the parent ResizeObserver detects the height change,
  // even when banners load asynchronously from API calls
  useLayoutEffect(() => {
    // Dispatch custom event to notify parent that banner height has changed
    // Use requestAnimationFrame to ensure DOM has fully updated
    const animationFrameId = requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent("topbar-banners-changed"));
    });
    return () => cancelAnimationFrame(animationFrameId);
  }, [visibleBanners.length]);

  if (visibleBanners.length === 0) return null;

  return (
    <div ref={containerRef}>
      {visibleBanners.map((banner: Banner) => {
        const bannerContent = getBannerContent(banner);
        if (!bannerContent) return null;

        const isDismissible = bannerContent.dismissible !== false; // Default to true if not specified
        const bannerKey =
          banner.bannerType === "pending invitation" && banner.invitation ? `${banner.bannerType}-${banner.invitation.ID}` : banner.bannerType;

        return (
          <StyledBanner
            key={bannerKey}
            data-testid={`user-management-banner-${banner.bannerType}`}
            severity={bannerContent.severity || "error"}
            icon={bannerContent.icon}
            action={
              <>
                {bannerContent.action}
                {isDismissible ? (
                  <IconButton onClick={() => handleClose(banner)} aria-label="Close Banner" color="inherit">
                    <Close />
                  </IconButton>
                ) : (
                  <IconButton disabled sx={{ visibility: "hidden" }} aria-hidden="true">
                    <Close />
                  </IconButton>
                )}
              </>
            }
          >
            {bannerContent.title && <AlertTitle sx={{ my: 0 }}>{bannerContent.title}</AlertTitle>}
            <Typography>{bannerContent.text}</Typography>
          </StyledBanner>
        );
      })}
    </div>
  );
};

export default UserManagementBanner;
